/* global __DEV__ */
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import Helmet from 'react-helmet'
import { leadMagnetPagesStatus } from 'reduxModules/leadMagnetPages'
import { leadMagnetPageStateSelector as stateSelector } from 'reduxModules/leadMagnetPages/selectors'
import { leadMagnetPageDispatchers as dispatchers } from 'reduxModules/leadMagnetPages/dispatchers'
import { TutorialSummaryPropTypes } from 'components/generic/CourseSummary/propTypes'

import withRouterV6 from '../../utils/withRouterV6'
import Video from '../generic/CourseSummary'
import BannerSlide from '../generic/BannerSlideShow/BannerSlide'
import VideoModal from '../generic/VideoModal'
import LeadMagnetFunnelForm from '../Tutorials/TutorialDetail/LeadMagnetFunnelForm'
import Hero from './Hero'
import Curriculum, { removeAfterDash, formatDuration } from './Curriculum'
import LockedLessons from './LockedLessons'
import TrialBlock from './TrialBlock'
import Testimonials from './Testimonials'
import PathChooser from './PathChooser'

import './styles.css'

// SPEC-052 Phase 1. The serializer's `show_signup_form` flag is the layout
// switch: false → the post-signup redesign (two hero actions, sequenced and
// locked lessons, trial block — FR-7..FR-12); true → the legacy layout with
// the opt-in form. A backend that predates the flag sends neither, which also
// renders legacy, so this deploys safely ahead of backend MR !661.
export class LeadMagnetPage extends Component {
  componentDidMount() {
    this.props.loadLeadMagnetPage()
    this.props.restoreStoredPath()
    this.maybeLoadTestimonials()
  }

  componentDidUpdate() {
    this.maybeLoadTestimonials()
  }

  // proof fallback for redesigned pages only, requested once — the flag
  // arrives with the page detail, so mount alone can't decide
  maybeLoadTestimonials() {
    if (this.props.showSignupForm === false && !this.testimonialsRequested) {
      this.testimonialsRequested = true
      this.props.loadTestimonials()
    }
  }

  playVideo(video) {
    this.props.loadLeadMagnetPageVideo({
      urlHi: video.videoHi,
      urlLow: video.videoLow,
      vtt: video.vttFile,
      subtitles: video.subtitles
    })
  }

  renderLessonTiles(lessons) {
    return lessons.map((video) => (
      <Video
        key={`video-${video.slug}`}
        className="video"
        slug={video.slug}
        teacher={video.artist.title}
        teacherSlug={video.artist.slug}
        title={video.title}
        thumb={video.firstFrameImage.default}
        isQuickPlay
        onQuickPlayClick={() => this.playVideo(video)}
      />
    ))
  }

  // deck slide 4 tile: thumb, then "LESSON N · ARTIST" eyebrow, then title —
  // the whole tile plays the lesson
  renderRedesignTiles(lessons) {
    return lessons.map((video) => {
      const eyebrow = [
        video.order != null && `Lesson ${video.order}`,
        video.artist?.title,
        video.durationSeconds > 0 && formatDuration(video.durationSeconds)
      ]
        .filter(Boolean)
        .join(' · ')
      return (
        <button
          key={`video-${video.slug}`}
          type="button"
          className="lm-lesson-tile"
          onClick={() => this.playVideo(video)}
          aria-label={`Play ${video.title}`}
        >
          <span
            className="lm-lesson-tile__thumb"
            style={
              video.firstFrameImage?.default
                ? { backgroundImage: `url(${video.firstFrameImage.default})` }
                : undefined
            }
          >
            <i className="icon-play" aria-hidden="true" />
          </span>
          {eyebrow && (
            <span className="lm-lesson-tile__eyebrow">{eyebrow}</span>
          )}
          <span className="lm-lesson-tile__title">{video.title}</span>
        </button>
      )
    })
  }

  // SPEC-053 FR-6 proof cascade: the page's curated list (admin M2M, in
  // marketing's order) → the single page-level trial_testimonial → the
  // first two published rows from the testimonials API. An unconfigured
  // page still shows real proof; curating later upgrades it — the last
  // tier exists so the section never renders empty, and reaching it in
  // production means a content task was skipped, so dev logs the tier.
  proofTestimonials() {
    let tier
    let proof
    if (this.props.testimonials?.length) {
      tier = 'page-curated list'
      proof = this.props.testimonials.slice(0, 3)
    } else if (this.props.trialTestimonial) {
      tier = 'trial_testimonial'
      proof = [this.props.trialTestimonial]
    } else {
      tier = 'FALLBACK first-2-published (curate the page testimonials!)'
      proof = (this.props.apiTestimonials || []).slice(0, 2)
    }
    if (__DEV__ && proof.length && this.loggedProofTier !== tier) {
      this.loggedProofTier = tier
      // eslint-disable-next-line no-console
      console.info(`[LeadMagnetPage] testimonial proof tier: ${tier}`)
    }
    return proof
  }

  renderRedesign() {
    const { banner, paths, chapters, chosenPathSlug, hasSubscription } =
      this.props
    const hasChooser = paths.length > 0
    const chosenPath =
      hasChooser && paths.find((path) => path.slug === chosenPathSlug)

    // SPEC-053 (+ Pedro's iteration): a page with a featured tutorial leads
    // with "Your free lessons" — the free chapters as a prominent playable
    // section — followed by the course's complete curriculum with those
    // same chapters unlocked in place. The curated locked tiles stay
    // superseded (the curriculum shows every chapter). Pages without a
    // featured tutorial keep the SPEC-052 layout: chooser sequencing or
    // free grid + curated locked tiles.
    const hasCurriculum = (chapters?.length ?? 0) > 0

    // With a chooser, nothing but the question shows until a path is picked;
    // then the page sequences to that path's lessons (FR-11). Without one
    // (Funnel A), the page's own free lessons and locked list show directly.
    // On curriculum pages the free section derives from the same chapters,
    // so both sections always agree.
    const lessons = hasCurriculum
      ? chapters
          .filter((chapter) => chapter.free && chapter.video)
          .map((chapter) => ({
            ...chapter.video,
            order: chapter.number,
            title: removeAfterDash(chapter.title),
            durationSeconds: chapter.durationSeconds,
            artist: null,
            firstFrameImage: { default: chapter.thumbnail?.default }
          }))
      : hasChooser
        ? (chosenPath?.videos ?? [])
        : this.props.videos
    const lockedLessons = hasChooser
      ? (chosenPath?.lockedLessons ?? [])
      : this.props.lockedLessons
    const lessonsTitle = hasChooser
      ? chosenPath && `Your ${chosenPath.title} path`
      : this.props.freeLessonsTitle

    // A page flipped to the redesign before its hero copy is entered (the
    // replicated-production state of the legacy pages) keeps the copy its
    // legacy banner already carried, and on a plain free grid the eyebrow
    // states the count the same way the curriculum pages' does.
    const heroHeading =
      this.props.heroHeading || banner?.title || this.props.title
    const heroSubheading = this.props.heroSubheading || banner?.description
    const heroEyebrow =
      this.props.heroEyebrow ||
      (!hasCurriculum && !hasChooser && lessons.length > 0
        ? `Free content · ${lessons.length} lesson${lessons.length === 1 ? '' : 's'}`
        : '')

    return (
      <>
        <Hero
          eyebrow={heroEyebrow}
          heading={heroHeading}
          subheading={heroSubheading}
          watchCtaLabel={this.props.watchCtaLabel}
          trialCtaLabel={this.props.trialCtaLabel}
          trialCtaUrl={this.props.trialCtaUrl}
          backgroundImage={banner?.asset?.original || ''}
          showWatchCta={lessons.length > 0}
          showTrialCta={!hasSubscription}
          onWatchFirst={() => this.playVideo(lessons[0])}
        />
        {!hasCurriculum && hasChooser && (
          <PathChooser
            question={this.props.chooserQuestion}
            paths={paths}
            chosenSlug={chosenPathSlug}
            onChoose={(path) => this.props.choosePath(path.slug, path.tag)}
          />
        )}
        {/* the deck's opening section: what the visitor just unlocked —
            on curriculum pages, derived from the free chapters */}
        {lessons.length > 0 && (
          <section id="videos" className="wrapper">
            {lessonsTitle && (
              <h2 className="lm-lessons__heading">{lessonsTitle}</h2>
            )}
            <div className="lm-lessons__grid">
              {this.renderRedesignTiles(lessons)}
            </div>
          </section>
        )}
        {hasCurriculum && (
          <Curriculum
            chapters={chapters}
            chaptersCount={this.props.chaptersCount || chapters.length}
            trialCtaUrl={this.props.trialCtaUrl}
            onPlay={(video) => this.playVideo(video)}
            onLockedClick={(chapter) => this.props.trackLockedChapter(chapter)}
          />
        )}
        {!hasCurriculum && (!hasChooser || !!chosenPath) && (
          <LockedLessons
            title={hasChooser ? 'Next on this path' : this.props.lockedSectionTitle}
            lessons={lockedLessons}
          />
        )}
        {/* on chooser pages the trial block is the closing step of the
            chosen sequence — its copy assumes a path, so it waits for the
            choice. The heading falls back like the serializer's own CTA
            defaults do: a redesigned page without its trial copy entered yet
            (the replicated-production state) must still make the ask —
            FR-8 is the point of the page. */}
        {!hasSubscription && (!hasChooser || !!chosenPath) && (
          <>
            <TrialBlock
              heading={this.props.trialHeading || 'Want the rest of the course?'}
              body={this.props.trialBody}
              ctaLabel={this.props.trialCtaLabel}
              ctaUrl={this.props.trialCtaUrl}
              reassurance={this.props.trialReassurance}
            />
            <Testimonials testimonials={this.proofTestimonials()} />
          </>
        )}
      </>
    )
  }

  renderLegacy() {
    const { banner, videos } = this.props
    return (
      <>
        <BannerSlide
          {...banner}
          video={banner.video}
          displayVideo={() => this.toggleVideo(banner.video)}
        />
        <section className="page-title wrapper heading">
          <h1>{this.props.title}</h1>
        </section>
        <section id="videos" className="wrapper">
          {this.renderLessonTiles(videos)}
        </section>
        <LeadMagnetFunnelForm tag={this.props.tag} />
      </>
    )
  }

  render() {
    if (this.props.status !== leadMagnetPagesStatus.success) return null

    const { player } = this.props
    // `show_signup_form: false` is the redesign; true or absent (an older
    // backend) keeps the page exactly as it is today
    const redesigned = this.props.showSignupForm === false
    const metaTitle = this.props.title || this.props.subtitle
    const metaDescription = this.props.subtitle

    return (
      <div id="LeadMagnetPage" className={redesigned ? '-post-signup' : ''}>
        <Helmet>
          <title>{metaTitle} | Stan Winston School of Character Arts</title>
          <meta name="description" content={metaDescription} />
          <meta property="og:title" content={this.props.fbTitle || metaTitle} />
          <meta
            property="og:description"
            content={this.props.fbDescription || metaDescription}
          />
          <meta content="article" property="og:type" />
        </Helmet>
        {redesigned ? this.renderRedesign() : this.renderLegacy()}
        {player.isPlaying && (
          <VideoModal
            close={() => this.props.clearLeadMagnetPageVideo()}
            urlHi={player.urlHi}
            urlLow={player.urlLow}
            subtitles={player.subtitles}
            vtt={player.vtt}
            visible={player.isPlaying}
          />
        )}
      </div>
    )
  }
}

const lockedLessonShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  order: PropTypes.number,
  title: PropTypes.string.isRequired,
  artist: PropTypes.shape({ title: PropTypes.string }),
  tutorial: PropTypes.shape({ slug: PropTypes.string, title: PropTypes.string }),
  thumbnail: PropTypes.shape({ default: PropTypes.string })
})

LeadMagnetPage.propTypes = {
  loadLeadMagnetPage: PropTypes.func.isRequired,
  restoreStoredPath: PropTypes.func.isRequired,
  choosePath: PropTypes.func.isRequired,
  status: PropTypes.string,
  subtitle: PropTypes.string,
  banner: PropTypes.shape({
    asset: PropTypes.shape({
      original: PropTypes.string,
      default: PropTypes.string,
      mobile: PropTypes.string,
      mobilexs: PropTypes.string,
      normal: PropTypes.string
    }),
    countdownDate: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaUrl: PropTypes.string,
    description: PropTypes.string,
    id: PropTypes.number,
    link: PropTypes.string,
    orderDate: PropTypes.string,
    title: PropTypes.string,
    hasGradient: PropTypes.bool
  }).isRequired,
  slug: PropTypes.string,
  body: PropTypes.string,
  videos: PropTypes.arrayOf(PropTypes.object),
  tutorials: PropTypes.arrayOf(PropTypes.shape(TutorialSummaryPropTypes))
    .isRequired,
  isMobile: PropTypes.bool,
  tag: PropTypes.string,
  hasSubscription: PropTypes.bool,
  // SPEC-052 post-signup redesign content (backend MR !661)
  showSignupForm: PropTypes.bool,
  heroEyebrow: PropTypes.string,
  heroHeading: PropTypes.string,
  heroSubheading: PropTypes.string,
  watchCtaLabel: PropTypes.string,
  trialCtaLabel: PropTypes.string,
  trialCtaUrl: PropTypes.string,
  trialHeading: PropTypes.string,
  trialBody: PropTypes.string,
  trialReassurance: PropTypes.string,
  trialTestimonial: PropTypes.shape({
    text: PropTypes.string,
    fullName: PropTypes.string,
    position: PropTypes.string
  }),
  // SPEC-053: page-curated testimonial list (backend M2M, when it lands)
  testimonials: PropTypes.arrayOf(PropTypes.object),
  // global published testimonials, the fallback proof tier
  apiTestimonials: PropTypes.arrayOf(PropTypes.object),
  loadTestimonials: PropTypes.func.isRequired,
  // SPEC-053: the featured course's complete chapter list (backend MR !664)
  featuredTutorial: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string
  }),
  chapters: PropTypes.arrayOf(PropTypes.object),
  chaptersCount: PropTypes.number,
  freeChaptersCount: PropTypes.number,
  trackLockedChapter: PropTypes.func.isRequired,
  chooserQuestion: PropTypes.string,
  freeLessonsTitle: PropTypes.string,
  lockedSectionTitle: PropTypes.string,
  lockedLessons: PropTypes.arrayOf(lockedLessonShape),
  paths: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      tag: PropTypes.string,
      videos: PropTypes.arrayOf(PropTypes.object),
      lockedLessons: PropTypes.arrayOf(lockedLessonShape)
    })
  ),
  chosenPathSlug: PropTypes.string
}

LeadMagnetPage.defaultProps = {
  body: '',
  videos: [],
  banner: {
    medium: ''
  },
  tutorials: [],
  slug: '',
  subtitle: '',
  isMobile: false,
  tag: 'Lead Magnet Email Lead',
  hasSubscription: false,
  showSignupForm: undefined,
  heroEyebrow: '',
  heroHeading: '',
  heroSubheading: '',
  watchCtaLabel: 'Watch lesson 1',
  trialCtaLabel: 'Start 7-day free trial',
  trialCtaUrl: '/subscriptions/plans',
  trialHeading: '',
  trialBody: '',
  trialReassurance: '',
  trialTestimonial: null,
  testimonials: [],
  apiTestimonials: [],
  featuredTutorial: null,
  chapters: null,
  chaptersCount: 0,
  freeChaptersCount: 0,
  chooserQuestion: 'What do you want to make first?',
  // the serializer has no fields for these section labels, so they are
  // frontend constants (deck slide 4 wording) rather than empty on real data
  freeLessonsTitle: 'Your free lessons',
  lockedSectionTitle: 'The rest of the course',
  lockedLessons: [],
  paths: [],
  chosenPathSlug: null
}

export default withRouterV6(connect(stateSelector, dispatchers)(LeadMagnetPage))
